import React, { useState, useEffect } from 'react';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import { Header, ViewMode } from './components/layout/Header';
import { BottomNav, CustomerTabType } from './components/layout/BottomNav';
import { AdminBottomNav, AdminTabType } from './components/layout/AdminBottomNav';
import { ShopPage } from './pages/ShopPage';
import { WalletPage } from './pages/WalletPage';
import { OrdersPage } from './pages/OrdersPage';
import { AdminPackagesPage } from './pages/admin/AdminPackagesPage';
import { AdminOrdersPage } from './pages/admin/AdminOrdersPage';
import { AdminDepositsPage } from './pages/admin/AdminDepositsPage';
import { AdminBuybacksPage } from './pages/admin/AdminBuybacksPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminCustomersPage } from './pages/admin/AdminCustomersPage';

const ADMIN_TABS: AdminTabType[] = ['admin-dashboard', 'admin-packages', 'admin-orders', 'admin-deposits', 'admin-buybacks', 'admin-customers'];
const CUSTOMER_TABS: CustomerTabType[] = ['shop', 'wallet', 'orders'];

function parseInitialRoute(): { viewMode: ViewMode; customerTab: CustomerTabType; adminTab: AdminTabType } {
  const hash = window.location.hash.replace('#', '').toLowerCase();
  const path = window.location.pathname.toLowerCase();
  const savedMode = (localStorage.getItem('pluvp_view_mode') as ViewMode) || 'customer';

  // Check if hash matches an admin tab
  if (ADMIN_TABS.includes(hash as AdminTabType)) {
    return { viewMode: 'admin', customerTab: 'shop', adminTab: hash as AdminTabType };
  }
  if (hash === 'admin' || path.startsWith('/admin')) {
    return { viewMode: 'admin', customerTab: 'shop', adminTab: 'admin-dashboard' };
  }

  // Check if hash matches a customer tab
  if (CUSTOMER_TABS.includes(hash as CustomerTabType)) {
    return { viewMode: 'customer', customerTab: hash as CustomerTabType, adminTab: 'admin-dashboard' };
  }
  if (path.startsWith('/wallet')) {
    return { viewMode: 'customer', customerTab: 'wallet', adminTab: 'admin-dashboard' };
  }
  if (path.startsWith('/orders')) {
    return { viewMode: 'customer', customerTab: 'orders', adminTab: 'admin-dashboard' };
  }

  return {
    viewMode: savedMode === 'admin' ? 'admin' : 'customer',
    customerTab: 'shop',
    adminTab: 'admin-dashboard',
  };
}

export const AppContent: React.FC = () => {
  const { isAdmin } = useAuth();
  const [initial] = useState(parseInitialRoute);
  const [viewMode, setViewMode] = useState<ViewMode>(initial.viewMode);
  const [customerTab, setCustomerTab] = useState<CustomerTabType>(initial.customerTab);
  const [adminTab, setAdminTab] = useState<AdminTabType>(initial.adminTab);

  // Guard: If not admin, restrict viewMode to 'customer'
  useEffect(() => {
    if (!isAdmin && viewMode === 'admin') {
      setViewMode('customer');
    }
  }, [isAdmin, viewMode]);

  // Sync route on popstate and hashchange
  useEffect(() => {
    const handleRouteChange = () => {
      const route = parseInitialRoute();
      if (isAdmin && route.viewMode === 'admin') {
        setViewMode('admin');
        setAdminTab(route.adminTab);
      } else {
        setViewMode('customer');
        setCustomerTab(route.customerTab);
      }
    };

    window.addEventListener('hashchange', handleRouteChange);
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('hashchange', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [isAdmin]);

  const handleSelectViewMode = (mode: ViewMode) => {
    if (mode === 'admin' && !isAdmin) return;
    setViewMode(mode);
    localStorage.setItem('pluvp_view_mode', mode);

    if (mode === 'admin') {
      window.location.hash = `#${adminTab}`;
    } else {
      window.location.hash = `#${customerTab}`;
    }
  };

  const handleSelectCustomerTab = (tab: CustomerTabType) => {
    setCustomerTab(tab);
    window.location.hash = `#${tab}`;
  };

  const handleSelectAdminTab = (tab: AdminTabType) => {
    setAdminTab(tab);
    window.location.hash = `#${tab}`;
  };

  return (
    <div className="min-h-screen bg-surface-page flex flex-col justify-between selection:bg-primary selection:text-white">
      <Header
        viewMode={viewMode}
        onSelectViewMode={handleSelectViewMode}
      />

      {/* View Mode Switching: Customer Store vs Admin Operations */}
      {viewMode === 'customer' || !isAdmin ? (
        <>
          <main className="w-full max-w-md mx-auto px-4 pt-4 flex-1">
            {customerTab === 'shop' && (
              <ShopPage onNavigateToOrders={() => handleSelectCustomerTab('orders')} />
            )}
            {customerTab === 'wallet' && <WalletPage />}
            {customerTab === 'orders' && <OrdersPage />}
          </main>
          <BottomNav activeTab={customerTab} onSelectTab={handleSelectCustomerTab} />
        </>
      ) : (
        <AdminProvider>
          <main className="w-full max-w-md md:max-w-xl mx-auto px-4 pt-4 flex-1">
            {adminTab === 'admin-dashboard' && (
              <AdminDashboardPage onNavigateTab={handleSelectAdminTab} />
            )}
            {adminTab === 'admin-packages' && <AdminPackagesPage />}
            {adminTab === 'admin-orders' && <AdminOrdersPage />}
            {adminTab === 'admin-deposits' && <AdminDepositsPage />}
            {adminTab === 'admin-buybacks' && <AdminBuybacksPage />}
            {adminTab === 'admin-customers' && <AdminCustomersPage />}
          </main>
          <AdminBottomNav activeTab={adminTab} onSelectTab={handleSelectAdminTab} />
        </AdminProvider>
      )}
    </div>
  );
};

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}
